import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const TextPromotion = () => {
    // --- State ---
    const [texts, setTexts] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // --- Modal State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [textInput, setTextInput] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadMode, setUploadMode] = useState('ADD');
    const [editId, setEditId] = useState(null);

    // --- Hooks & Context ---
    // Added userType to determine role
    const { appId, language, userId, userType } = useAppId();
    const lang = language || 'en';

    // --- Role Verification Logic ---
    const activeRole = userType || localStorage.getItem('userType') || localStorage.getItem('designation') || localStorage.getItem('role');
    const roleCode = activeRole ? activeRole.toUpperCase() : 'A';
    const isSubAdmin = roleCode === 'SA' || roleCode === 'SUB_ADMIN';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Text Promotion",
            subtitle: "View and share promotional messages",
            uploadedBy: "Added By :",
            loadingData: "Loading Messages...",
            noData: "No promotional messages found.",
            btnAdd: "Add Message",
            btnChange: "Edit Message",
            btnDelete: "Delete Message",
            modalTitleAdd: "Create New Message",
            modalTitleEdit: "Edit Message",
            inputPlaceholder: "Type your promotional message here...",
            btnCancel: "Cancel",
            btnSubmit: "Save Message",
            adding: "Saving..."
        },
        hi: {
            pageTitle: "पाठ प्रचार",
            subtitle: "प्रचार संदेश देखें और साझा करें",
            uploadedBy: "द्वारा जोड़ा गया :",
            loadingData: "संदेश लोड हो रहे हैं...",
            noData: "कोई प्रचार संदेश नहीं मिला।",
            btnAdd: "संदेश जोड़ें",
            btnChange: "संदेश संपादित करें",
            btnDelete: "संदेश हटाएं",
            modalTitleAdd: "नया संदेश बनाएं",
            modalTitleEdit: "संदेश संपादित करें",
            inputPlaceholder: "अपना प्रचार संदेश यहाँ टाइप करें...",
            btnCancel: "रद्द करें",
            btnSubmit: "संदेश सहेजें",
            adding: "सहेज रहा है..."
        },
        gu: {
            pageTitle: "ટેક્સ્ટ પ્રમોશન",
            subtitle: "પ્રમોશનલ સંદેશાઓ જુઓ અને શેર કરો",
            uploadedBy: "ઉમેરનાર :",
            loadingData: "સંદેશાઓ લોડ થઈ રહ્યા છે...",
            noData: "કોઈ પ્રમોશનલ સંદેશ મળ્યો નથી.",
            btnAdd: "સંદેશ ઉમેરો",
            btnChange: "સંદેશ સંપાદિત કરો",
            btnDelete: "સંદેશ કાઢી નાખો",
            modalTitleAdd: "નવો સંદેશ બનાવો",
            modalTitleEdit: "સંદેશ સંપાદિત કરો",
            inputPlaceholder: "તમારો પ્રમોશનલ સંદેશ અહીં લખો...",
            btnCancel: "રદ કરો",
            btnSubmit: "સંદેશ સાચવો",
            adding: "સાચવી રહ્યું છે..."
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchTexts = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_prachar_master`,
                { 
                    app_id: appId,
                    prachar_type: "text" // Text promotion type
                }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setTexts(response.data.data.result);
            } else {
                setTexts([]);
            }
        } catch (error) {
            console.error("Error fetching text promotions:", error);
            setTexts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTexts();
    }, [appId]);

    // --- Global Add / Edit Action (Modal Logic) ---
    const handleOpenAddModal = () => {
        setUploadMode('ADD');
        setEditId(null);
        setTextInput(""); 
        setIsModalOpen(true);
    };

    // UPDATED: Global Edit uses the fetched text directly
    const handleOpenEditModal = () => {
        if (texts.length > 0) {
            setUploadMode('CHANGE');
            setEditId(texts[0].id);
            setTextInput(texts[0].content);
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = () => {
        if (!isSubmitting) {
            setIsModalOpen(false);
            setTextInput("");
            setEditId(null);
        }
    };

    const handleSubmitText = async () => {
        if (!textInput || textInput.trim() === "") {
            alert("Please type a message before saving.");
            return;
        }

        try {
            setIsSubmitting(true);
            const payload = {
                app_id: appId?.toString(),
                prachar_type: "text",
                base64: "", // Empty because it's text
                content: textInput.trim(), // The typed message
                create_by: userId?.toString() || "1"
            };

            if (uploadMode === 'CHANGE' && editId) {
                payload.id = editId.toString();
            }

            console.log(`Sending ${uploadMode} Text Payload:`, payload);

            const response = await axios.post(
                `${BASE_URL}/api/ins_prachar_master`,
                payload
            );

            if (response.data.success) {
                await fetchTexts(); // Instantly reload to show the new message
                handleCloseModal(); // Close modal on success
            } else {
                alert("Failed to save the message. Server responded but success was false.");
            }
        } catch (error) {
            console.error("Error saving message:", error.response ? error.response.data : error.message);
            alert(`An error occurred while saving the message: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // UPDATED: Global Delete uses the fetched text directly
    const handleDeleteText = async () => {
        if (texts.length === 0) return;

        if (window.confirm("Are you sure you want to delete this message?")) {
            try {
                const textId = texts[0].id;

                const payload = {
                    app_id: appId?.toString(),
                    id: textId?.toString(),
                    delete_by: userId?.toString() || "1" 
                };

                const response = await axios.post(
                    `${BASE_URL}/api/del_prachar_master`,
                    payload
                );

                if (response.data.success) {
                    fetchTexts();
                } else {
                    alert("Failed to delete the message.");
                }
            } catch (error) {
                console.error("Error deleting message:", error);
                alert(`An error occurred while deleting the message.`);
            }
        }
    };

    // --- Loading State ---
    if (loading && texts.length === 0 && !isModalOpen) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #cbd5e1; border-top: 4px solid #a855f7; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header title={txt.pageTitle} />

            {/* Page Action Bar (Subtitle + Add Button) */}
            <div className="page-action-bar mt-3 mb-4">
                <div className="subtitle-container m-0">
                    <p className="subtitle-text">{txt.subtitle}</p>
                </div>
                
                <div className="global-buttons">
                    {/* CONDITIONAL RENDERING FOR BUTTONS - Hidden for Sub Admins */}
                    {!isSubAdmin && (
                        texts.length === 0 ? (
                            <button 
                                className="global-btn btn-add" 
                                onClick={handleOpenAddModal}
                            >
                                <svg fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                    <line x1="12" y1="9" x2="12" y2="15"></line>
                                    <line x1="9" y1="12" x2="15" y2="12"></line>
                                </svg>
                                {txt.btnAdd}
                            </button>
                        ) : (
                            <>
                                <button 
                                    className="global-btn btn-edit" 
                                    onClick={handleOpenEditModal}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <path d="M12 20h9"></path>
                                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                    </svg>
                                    {txt.btnChange}
                                </button>

                                <button 
                                    className="global-btn btn-delete" 
                                    onClick={handleDeleteText}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                    {txt.btnDelete}
                                </button>
                            </>
                        )
                    )}
                </div>
            </div>

            {/* No Data State */}
            {texts.length === 0 && !loading && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">💬</div>
                        <p>{txt.noData}</p>
                    </div>
                </div>
            )}

            {/* --- TEXT GRID --- */}
            <div className="text-grid pb-5">
                {texts.map((item) => (
                    <div className="text-card" key={item.id}>
                        {/* Text Container */}
                        <div className="text-wrapper">
                            <span className="quote-icon">❝</span>
                            <p className="promo-text-content">{item.content}</p>
                        </div>

                        {/* Uploader Details Container */}
                        <div className="info-wrapper">
                            <div className="upload-info">
                                <span className="upload-label">{txt.uploadedBy}</span>
                                <span className="upload-name">{item.name || "Unknown"}</span>
                            </div>
                            <div className="designation-badge">
                                {item.designation || "User"}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* --- CUSTOM MODAL UI FOR TEXT --- */}
            {isModalOpen && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {uploadMode === 'ADD' ? txt.modalTitleAdd : txt.modalTitleEdit}
                            </h3>
                            <button className="modal-close-btn" onClick={handleCloseModal} disabled={isSubmitting}>
                                &times;
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <textarea 
                                className="modal-textarea" 
                                placeholder={txt.inputPlaceholder}
                                value={textInput}
                                onChange={(e) => setTextInput(e.target.value)}
                                disabled={isSubmitting}
                                autoFocus
                            />
                        </div>

                        <div className="modal-footer">
                            <button 
                                className="modal-btn btn-secondary" 
                                onClick={handleCloseModal}
                                disabled={isSubmitting}
                            >
                                {txt.btnCancel}
                            </button>
                            <button 
                                className="modal-btn btn-primary" 
                                onClick={handleSubmitText}
                                disabled={isSubmitting || !textInput.trim()}
                            >
                                {isSubmitting ? txt.adding : txt.btnSubmit}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- PAGE ACTION BAR --- */
                .page-action-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .subtitle-text {
                    font-size: 15px;
                    color: #475569;
                    font-weight: 600;
                    margin: 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .global-buttons {
                    display: flex;
                    gap: 12px;
                }

                .global-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 16px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }

                .global-btn svg {
                    width: 16px;
                    height: 16px;
                }

                /* Add Button Global (Solid Purple Theme) */
                .btn-add {
                    background: #a855f7;
                    color: #ffffff;
                    border: 1px solid #9333ea;
                    box-shadow: 0 4px 6px -1px rgba(168, 85, 247, 0.2);
                }
                .btn-add:hover {
                    background: #9333ea;
                    border-color: #7e22ce;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 8px -1px rgba(168, 85, 247, 0.3);
                }
                .btn-add:active {
                    background: #7e22ce;
                    transform: translateY(0);
                }

                /* Edit Button Global */
                .btn-edit {
                    background: #ffffff;
                    color: #334155;
                    border: 1px solid #cbd5e1;
                }
                .btn-edit:hover {
                    background: #f8fafc;
                    color: #0f172a;
                    border-color: #94a3b8;
                }
                .btn-edit:active {
                    background: #f1f5f9;
                }

                /* Delete Button Global */
                .btn-delete {
                    background: #fef2f2;
                    color: #ef4444;
                    border: 1px solid #fecaca;
                }
                .btn-delete:hover {
                    background: #fee2e2;
                    color: #dc2626;
                    border-color: #fca5a5;
                }
                .btn-delete:active {
                    background: #fecaca;
                }

                /* --- MODAL UI CSS --- */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(15, 23, 42, 0.6);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    animation: fadeIn 0.2s ease-out;
                }

                .modal-box {
                    background: #ffffff;
                    width: 90%;
                    max-width: 500px;
                    border-radius: 16px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    overflow: hidden;
                    animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px 24px;
                    border-bottom: 1px solid #f1f5f9;
                }

                .modal-title {
                    margin: 0;
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #0f172a;
                }

                .modal-close-btn {
                    background: transparent;
                    border: none;
                    font-size: 1.5rem;
                    line-height: 1;
                    color: #94a3b8;
                    cursor: pointer;
                    transition: color 0.2s ease;
                }

                .modal-close-btn:hover {
                    color: #0f172a;
                }

                .modal-body {
                    padding: 24px;
                }

                .modal-textarea {
                    width: 100%;
                    min-height: 150px;
                    padding: 16px;
                    font-size: 15px;
                    color: #334155;
                    background: #f8fafc;
                    border: 1px solid #cbd5e1;
                    border-radius: 10px;
                    outline: none;
                    resize: vertical;
                    transition: all 0.2s ease;
                    font-family: inherit;
                    line-height: 1.5;
                }

                .modal-textarea:focus {
                    background: #ffffff;
                    border-color: #a855f7;
                    box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.15);
                }

                .modal-footer {
                    padding: 16px 24px;
                    background: #f8fafc;
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    border-top: 1px solid #f1f5f9;
                }

                .modal-btn {
                    padding: 10px 20px;
                    font-size: 14px;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .modal-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-secondary {
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    color: #475569;
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f1f5f9;
                    color: #0f172a;
                }

                .btn-primary {
                    background: #a855f7;
                    border: 1px solid #9333ea;
                    color: #ffffff;
                }

                .btn-primary:hover:not(:disabled) {
                    background: #9333ea;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* --- TEXT GRID --- */
                .text-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 24px;
                }

                /* --- PREMIUM CARD STYLES --- */
                .text-card {
                    background: #ffffff;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .text-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1);
                    border-color: #e2e8f0;
                }

                /* Text Display Container */
                .text-wrapper {
                    width: 100%;
                    min-height: 200px;
                    max-height: 300px;
                    padding: 24px 30px;
                    background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%); /* Soft Purple Gradient */
                    position: relative;
                    overflow-y: auto; /* Allow scrolling for long messages */
                    border-bottom: 1px solid #e9d5ff;
                }

                .text-wrapper::-webkit-scrollbar {
                    width: 6px;
                }
                .text-wrapper::-webkit-scrollbar-thumb {
                    background-color: #d8b4fe;
                    border-radius: 10px;
                }

                .quote-icon {
                    position: absolute;
                    top: 10px;
                    left: 14px;
                    font-size: 3rem;
                    color: #d8b4fe;
                    opacity: 0.5;
                    font-family: serif;
                    line-height: 1;
                }

                .promo-text-content {
                    font-size: 16px;
                    line-height: 1.6;
                    color: #334155;
                    position: relative;
                    z-index: 1;
                    margin: 0;
                    white-space: pre-wrap; /* Keeps formatting and line breaks */
                }

                /* Bottom Info Area */
                .info-wrapper {
                    padding: 16px 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    background: #ffffff;
                }

                .upload-info {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .upload-label {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 500;
                }

                .upload-name {
                    font-size: 15px;
                    color: #0f172a;
                    font-weight: 700;
                }

                /* Designation Badge */
                .designation-badge {
                    align-self: flex-start;
                    font-size: 11px;
                    font-weight: 700;
                    color: #9333ea; /* Match Purple Theme */
                    background: #faf5ff;
                    padding: 4px 10px;
                    border-radius: 20px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border: 1px solid #e9d5ff;
                }

                /* Empty State */
                .empty-state { 
                    background: white; 
                    border-radius: 20px; 
                    border: 2px dashed #cbd5e1; 
                }
                .empty-icon { 
                    font-size: 3rem; 
                    margin-bottom: 10px; 
                    opacity: 0.5; 
                }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .page-action-bar {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .global-buttons {
                        width: 100%;
                        display: flex;
                    }
                    .global-btn {
                        flex: 1;
                        justify-content: center;
                    }
                    .text-grid { 
                        grid-template-columns: 1fr; 
                        gap: 16px;
                    }
                }
            `}</style>
        </div>
    );
};

export default TextPromotion;